/**
 * A cardless capture: good frames are collected automatically, then combined robustly.
 *
 * Frames that pass every gate count towards the target; the median and MAD of their PDs find the stable centre, and frames far from it are dropped as outliers and replaced by more good frames. The capture then has to show it was steady — per-frame spread, drift between its halves, iris agreement, head stillness — or it asks for a retry instead of a number. Capture quality and scale uncertainty are reported apart: a sharp, steady capture can still be biased by the person's own iris size.
 */
import type { Pose } from '../pose';
import type { CaptureDiagnostics } from './diagnostics';
import { captureDiagnostics } from './diagnostics';
import type { CardlessMeasurement, CardlessOutcome, CardlessRejection } from './frame';
import { distanceFromConvergedPd, IRIS_REFERENCE_MM, pdIfIrisWere, pdString } from './scale';

export type FixationProtocol = 'CAMERA_LENS' | 'DISTANT_TARGET';

export interface CaptureSettings {
  targetGoodFrames: number;
  /** After outliers are dropped, at least this many must remain or more good frames are collected. */
  minKeptFrames: number;
  maxGoodFrames: number;
  /** Accepted frames closer together than this are skipped, so the capture spans the subject's natural movement rather than one instant. */
  minSpacingMs: number;
  timeoutMs: number;
  outlierMads: number;
  outlierFloorMm: number;
  stability: {
    maxFrameSdMm: number;
    maxStandardErrorMm: number;
    maxHalvesDiffMm: number;
    maxIrisRatioSd: number;
    maxYawSdDeg: number;
  };
}

export const CAPTURE_SETTINGS: CaptureSettings = {
  targetGoodFrames: 30,
  minKeptFrames: 25,
  maxGoodFrames: 60,
  minSpacingMs: 50,
  timeoutMs: 30000,
  outlierMads: 3,
  outlierFloorMm: 1,
  stability: {
    maxFrameSdMm: 1.5,
    maxStandardErrorMm: 0.35,
    maxHalvesDiffMm: 1,
    maxIrisRatioSd: 0.06,
    maxYawSdDeg: 2.5,
  },
};

export interface Spread {
  n: number;
  median: number;
  /** 1.4826 × MAD: the SD a normal distribution with this MAD would have. */
  robustSd: number;
  mad: number;
  min: number;
  max: number;
  mean: number;
  /** Sample standard deviation, or null below two values. */
  sd: number | null;
}

export function spread(xs: number[]): Spread | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const med = medianSorted(s);
  const mad = medianSorted([...xs.map((x) => Math.abs(x - med))].sort((a, b) => a - b));
  const mean = xs.reduce((t, x) => t + x, 0) / xs.length;
  return {
    n: xs.length,
    median: med,
    robustSd: 1.4826 * mad,
    mad,
    min: s[0]!,
    max: s[s.length - 1]!,
    mean,
    sd:
      xs.length > 1
        ? Math.sqrt(xs.reduce((t, x) => t + (x - mean) ** 2, 0) / (xs.length - 1))
        : null,
  };
}

export interface QualityTerm {
  name: string;
  value: number | null;
  /** The rule for HIGH, and for MEDIUM where the term is part of it. */
  high: string;
  passesHigh: boolean;
  medium?: string;
  passesMedium?: boolean;
}

export interface QualityExplanation {
  grade: CaptureQuality;
  /** The terms of the grading rule, exactly as it is applied. */
  terms: QualityTerm[];
  /** The terms that kept the capture from the next grade up. */
  limitedBy: string[];
}

/**
 * The capture-quality rule, term by term, so a grade can be explained. The rule is unchanged from 4B.0C 0.1: HIGH needs every HIGH term; MEDIUM needs its two terms.
 *
 * Its "not accepted" share counts every processed frame that did not become a good frame, including good frames skipped only because they came within the minimum spacing of the previous one.
 */
export function explainQuality(input: {
  perFrameRobustSdMm: number;
  standardErrorMm: number;
  minIrisPx: number;
  blurSigmaPx: number;
  processed: number;
  accepted: number;
}): QualityExplanation {
  const notAccepted = input.processed ? 1 - input.accepted / input.processed : 1;
  const terms: QualityTerm[] = [
    {
      name: 'Per-frame PD spread (robust SD, mm)',
      value: input.perFrameRobustSdMm,
      high: '≤ 0.8',
      passesHigh: input.perFrameRobustSdMm <= 0.8,
      medium: '≤ 1.2',
      passesMedium: input.perFrameRobustSdMm <= 1.2,
    },
    {
      name: 'Smaller iris diameter (px)',
      value: input.minIrisPx,
      high: '≥ 26',
      passesHigh: input.minIrisPx >= 26,
      medium: '≥ 22',
      passesMedium: input.minIrisPx >= 22,
    },
    {
      name: 'Limbus edge blur (sigma, px)',
      value: input.blurSigmaPx,
      high: '≤ 1.3',
      passesHigh: input.blurSigmaPx <= 1.3,
    },
    {
      name: 'Standard error of the median (mm)',
      value: input.standardErrorMm,
      high: '≤ 0.2',
      passesHigh: input.standardErrorMm <= 0.2,
    },
    {
      name: 'Processed frames not accepted (share)',
      value: notAccepted,
      high: '< 0.6',
      passesHigh: notAccepted < 0.6,
    },
  ];
  const high = terms.every((t) => t.passesHigh);
  const medium = terms.every((t) => t.passesMedium !== false);
  const grade: CaptureQuality = high ? 'HIGH' : medium ? 'MEDIUM' : 'LOW';
  const limitedBy =
    grade === 'HIGH'
      ? []
      : terms
          .filter((t) => (grade === 'MEDIUM' ? !t.passesHigh : t.passesMedium === false))
          .map((t) => t.name);
  return { grade, terms, limitedBy };
}

function medianSorted(s: number[]): number {
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

const median = (xs: number[]): number => medianSorted([...xs].sort((a, b) => a - b));

export interface Timing {
  model: number;
  total: number;
}

interface Accepted {
  t: number;
  m: CardlessMeasurement;
}

export type CaptureStatus = 'COMPLETE' | 'UNSTABLE' | 'TIMED_OUT' | 'CANCELLED';
export type CaptureQuality = 'HIGH' | 'MEDIUM' | 'LOW';

export interface StabilityReport {
  stable: boolean;
  perFrameRobustSdMm: number;
  madMm: number;
  standardErrorMm: number;
  halvesDiffMm: number;
  irisRatioRobustSd: number;
  yawSdDeg: number;
  failures: string[];
}

export interface CaptureResult {
  status: CaptureStatus;
  pdType: 'DISTANCE';
  /** Total distance PD as the product stores it: tenths, rounded half up. Null unless the capture completed and was steady. */
  totalPdMm: string | null;
  /** Monocular PD is not measured by this method: never derived from the total. */
  rightPdMm: null;
  leftPdMm: null;
  fixationProtocol: FixationProtocol;
  /** The PD the eyes showed while fixating the lens, before the convergence correction. */
  convergedPdMm: number | null;
  /** Added for CAMERA_LENS to undo the eyes turning inwards; zero for DISTANT_TARGET. */
  convergenceCorrectionMm: number | null;
  estimatedDistanceMm: number | null;
  distancePdMm: number | null;
  captureQuality: CaptureQuality | null;
  /** How the grade was reached, term by term, and what limited it; diagnostics only. */
  qualityExplanation: QualityExplanation | null;
  /** Biology, not capture: the same pixels for other plausible iris sizes. Never folded into captureQuality. */
  scaleUncertainty: {
    irisReferenceMm: number;
    ifIrisWere: { irisMm: number; pdMm: number }[];
    perTenthOfMmIris: number;
  } | null;
  stability: StabilityReport | null;
  frames: {
    processed: number;
    accepted: number;
    skippedForSpacing: number;
    used: number;
    outliers: number;
    rejected: number;
    reasons: Partial<Record<CardlessRejection, number>>;
    timeToCompleteMs: number | null;
  };
  pixels: {
    /** Per eye, "A" is the iris on the image's left. Nasal is the side towards the other eye. */
    perEye: Record<string, Spread | null>;
    irisAPx: Spread | null;
    irisBPx: Spread | null;
    ipdPx: Spread | null;
    irisRatio: Spread | null;
    disagreementPct: Spread | null;
    blurSigmaPx: Spread | null;
    contrast: Spread | null;
    residualPx: Spread | null;
    lateralArc: Spread | null;
    verticalAPx: Spread | null;
    verticalBPx: Spread | null;
  };
  pose: { yawDeg: Spread | null; pitchDeg: Spread | null; rollDeg: Spread | null };
  perFramePd: Spread | null;
  features: Record<string, number | null>;
  performance: {
    modelMs: Spread | null;
    pipelineMs: Spread | null;
    slowestMs: number | null;
    mediapipeFps: number | null;
  };
  /** R&D only (T1): the used frames' diagnostics, summarised after everything above is final; nothing above is computed from them. Null when no used frame carried any. */
  diagnostics: CaptureDiagnostics | null;
}

/** Diagnostics never cost a result: a failure while summarising them leaves them out. */
function summariseDiagnostics(used: CardlessMeasurement[]): CaptureDiagnostics | null {
  try {
    return captureDiagnostics(used);
  } catch {
    return null;
  }
}

export class CardlessCapture {
  private readonly accepted: Accepted[] = [];
  private readonly reasons: Partial<Record<CardlessRejection, number>> = {};
  private readonly timings: Timing[] = [];
  private processed = 0;
  private skipped = 0;
  private readonly startedAt: number;
  private completedAt: number | null = null;
  readonly fixation: FixationProtocol;
  readonly settings: CaptureSettings;
  readonly irisReferenceMm: number;

  constructor(
    fixation: FixationProtocol,
    startedAt = 0,
    settings: CaptureSettings = CAPTURE_SETTINGS,
    irisReferenceMm = IRIS_REFERENCE_MM,
  ) {
    this.fixation = fixation;
    this.startedAt = startedAt;
    this.settings = settings;
    this.irisReferenceMm = irisReferenceMm;
  }

  /** Adds one analysed frame; returns whether it counted as a good frame. */
  push(o: CardlessOutcome, timing?: Timing): boolean {
    this.processed += 1;
    if (timing) this.timings.push(timing);
    if (!o.ok) {
      this.reasons[o.reason] = (this.reasons[o.reason] ?? 0) + 1;
      return false;
    }
    const last = this.accepted[this.accepted.length - 1];
    if (last && o.t - last.t < this.settings.minSpacingMs) {
      this.skipped += 1;
      return false;
    }
    this.accepted.push({ t: o.t, m: o.frame });
    return true;
  }

  get good(): number {
    return this.accepted.length;
  }

  private kept(): { kept: Accepted[]; outliers: number } {
    const pds = this.accepted.map((a) => a.m.convergedPdMm);
    const s = spread(pds);
    if (!s) return { kept: [], outliers: 0 };
    const limit = Math.max(this.settings.outlierMads * s.robustSd, this.settings.outlierFloorMm);
    const kept = this.accepted.filter((a) => Math.abs(a.m.convergedPdMm - s.median) <= limit);
    return { kept, outliers: this.accepted.length - kept.length };
  }

  /** Enough good frames that survive the outlier cut, or no more room to try. */
  ready(now: number): boolean {
    if (now - this.startedAt > this.settings.timeoutMs) return true;
    if (this.accepted.length < this.settings.targetGoodFrames) return false;
    return (
      this.kept().kept.length >= this.settings.minKeptFrames ||
      this.accepted.length >= this.settings.maxGoodFrames
    );
  }

  finish(now: number, cancelled = false): CaptureResult {
    if (this.completedAt === null) this.completedAt = now;
    const { kept, outliers } = this.kept();
    if (outliers) this.reasons.PD_OUTLIER = (this.reasons.PD_OUTLIER ?? 0) + outliers;
    const enough =
      this.accepted.length >= this.settings.targetGoodFrames &&
      kept.length >= this.settings.minKeptFrames;
    const ms = kept.map((k) => k.m);
    const pds = ms.map((m) => m.convergedPdMm);
    const perFramePd = spread(pds);
    const stability = perFramePd ? this.stability(kept, perFramePd) : null;
    const status: CaptureStatus = cancelled
      ? 'CANCELLED'
      : !enough
        ? 'TIMED_OUT'
        : stability?.stable
          ? 'COMPLETE'
          : 'UNSTABLE';
    const convergedPdMm = perFramePd ? perFramePd.median : null;
    const estimatedDistanceMm = ms.length ? median(ms.map((m) => m.estimatedDistanceMm)) : null;
    let correction: number | null = null;
    let distancePdMm: number | null = null;
    if (convergedPdMm !== null && estimatedDistanceMm !== null) {
      if (this.fixation === 'CAMERA_LENS') {
        const c = distanceFromConvergedPd(convergedPdMm, estimatedDistanceMm);
        correction = c.correctionMm;
        distancePdMm = c.distancePdMm;
      } else {
        correction = 0;
        distancePdMm = convergedPdMm;
      }
    }
    const pixels = {
      perEye: {
        blurA: spread(ms.map((m) => m.fitA.blurSigmaPx)),
        blurB: spread(ms.map((m) => m.fitB.blurSigmaPx)),
        contrastA: spread(ms.map((m) => m.fitA.contrast)),
        contrastB: spread(ms.map((m) => m.fitB.contrast)),
        residualA: spread(ms.map((m) => m.fitA.residualPx)),
        residualB: spread(ms.map((m) => m.fitB.residualPx)),
        nasalArcA: spread(ms.map((m) => m.fitA.lateralArc.forward)),
        temporalArcA: spread(ms.map((m) => m.fitA.lateralArc.backward)),
        nasalArcB: spread(ms.map((m) => m.fitB.lateralArc.backward)),
        temporalArcB: spread(ms.map((m) => m.fitB.lateralArc.forward)),
        nasalRadiusA: spread(ms.map((m) => m.fitA.sideRadiusPx.forward)),
        temporalRadiusA: spread(ms.map((m) => m.fitA.sideRadiusPx.backward)),
        nasalRadiusB: spread(ms.map((m) => m.fitB.sideRadiusPx.backward)),
        temporalRadiusB: spread(ms.map((m) => m.fitB.sideRadiusPx.forward)),
        pdFromAMm: spread(ms.map((m) => m.perEye.pdFromAMm)),
        pdFromBMm: spread(ms.map((m) => m.perEye.pdFromBMm)),
        xFromCentreA: spread(ms.map((m) => m.perEye.aFromCentrePx[0])),
        xFromCentreB: spread(ms.map((m) => m.perEye.bFromCentrePx[0])),
        yFromCentreA: spread(ms.map((m) => m.perEye.aFromCentrePx[1])),
        yFromCentreB: spread(ms.map((m) => m.perEye.bFromCentrePx[1])),
        radialFromCentreA: spread(ms.map((m) => Math.hypot(...m.perEye.aFromCentrePx))),
        radialFromCentreB: spread(ms.map((m) => Math.hypot(...m.perEye.bFromCentrePx))),
        lateralArcA: spread(ms.map((m) => m.fitA.lateralArc.both)),
        lateralArcB: spread(ms.map((m) => m.fitB.lateralArc.both)),
      },
      irisAPx: spread(ms.map((m) => m.irisAPx)),
      irisBPx: spread(ms.map((m) => m.irisBPx)),
      ipdPx: spread(ms.map((m) => m.ipdPx)),
      irisRatio: spread(ms.map((m) => m.irisAPx / m.irisBPx)),
      disagreementPct: spread(
        ms.map((m) => (100 * Math.abs(m.irisAPx - m.irisBPx)) / m.irisMeanPx),
      ),
      blurSigmaPx: spread(ms.flatMap((m) => [m.fitA.blurSigmaPx, m.fitB.blurSigmaPx])),
      contrast: spread(ms.flatMap((m) => [m.fitA.contrast, m.fitB.contrast])),
      residualPx: spread(ms.flatMap((m) => [m.fitA.residualPx, m.fitB.residualPx])),
      lateralArc: spread(ms.flatMap((m) => [m.fitA.lateralArc.both, m.fitB.lateralArc.both])),
      verticalAPx: spread(
        ms.flatMap((m) => (m.fitA.verticalDiameterPx === null ? [] : [m.fitA.verticalDiameterPx])),
      ),
      verticalBPx: spread(
        ms.flatMap((m) => (m.fitB.verticalDiameterPx === null ? [] : [m.fitB.verticalDiameterPx])),
      ),
    };
    const pick = (f: (p: Pose) => number) => spread(ms.map((m) => f(m.pose)));
    const feature = (f: (m: CardlessMeasurement) => number | null) => {
      const xs = ms.map(f).filter((x): x is number => x !== null && Number.isFinite(x));
      return xs.length ? median(xs) : null;
    };
    const modelMs = spread(this.timings.map((t) => t.model).filter((x) => x > 0));
    const pipelineMs = spread(this.timings.map((t) => t.total));
    const complete = status === 'COMPLETE';
    return {
      status,
      pdType: 'DISTANCE',
      totalPdMm: complete && distancePdMm !== null ? pdString(distancePdMm) : null,
      rightPdMm: null,
      leftPdMm: null,
      fixationProtocol: this.fixation,
      convergedPdMm,
      convergenceCorrectionMm: correction,
      estimatedDistanceMm,
      distancePdMm,
      captureQuality: complete && stability && perFramePd ? this.quality(stability, pixels) : null,
      qualityExplanation: stability && perFramePd ? this.explain(stability, pixels) : null,
      scaleUncertainty:
        distancePdMm === null
          ? null
          : {
              irisReferenceMm: this.irisReferenceMm,
              ifIrisWere: [11.1, 11.3, 11.5, 11.7, 11.9, 12.1, 12.3].map((irisMm) => ({
                irisMm,
                pdMm: pdIfIrisWere(distancePdMm, irisMm, this.irisReferenceMm),
              })),
              perTenthOfMmIris: (distancePdMm * 0.1) / this.irisReferenceMm,
            },
      stability,
      frames: {
        processed: this.processed,
        accepted: this.accepted.length,
        skippedForSpacing: this.skipped,
        used: kept.length,
        outliers,
        rejected: Object.entries(this.reasons).reduce(
          (s, [k, v]) => s + (k === 'PD_OUTLIER' ? 0 : (v ?? 0)),
          0,
        ),
        reasons: { ...this.reasons },
        timeToCompleteMs: enough ? this.completedAt - this.startedAt : null,
      },
      pixels,
      pose: {
        yawDeg: pick((p) => p.yawDeg),
        pitchDeg: pick((p) => p.pitchDeg),
        rollDeg: pick((p) => p.rollDeg),
      },
      perFramePd,
      features: {
        innerCanthalToIpd: feature((m) => m.features.innerCanthalToIpd),
        outerCanthalToIpd: feature((m) => m.features.outerCanthalToIpd),
        faceWidthToIpd: feature((m) => m.features.faceWidthToIpd),
        irisToEyeWidth: feature((m) => m.features.irisToEyeWidth),
      },
      performance: {
        modelMs,
        pipelineMs,
        slowestMs: this.timings.length ? Math.max(...this.timings.map((t) => t.total)) : null,
        mediapipeFps: modelMs ? 1000 / modelMs.median : null,
      },
      diagnostics: summariseDiagnostics(ms),
    };
  }

  private stability(kept: Accepted[], pd: Spread): StabilityReport {
    const st = this.settings.stability;
    const n = kept.length;
    const half = Math.floor(n / 2);
    const first = kept.slice(0, half).map((k) => k.m.convergedPdMm);
    const second = kept.slice(half).map((k) => k.m.convergedPdMm);
    const halvesDiffMm =
      first.length && second.length ? Math.abs(median(first) - median(second)) : 0;
    const ratio = spread(kept.map((k) => k.m.irisAPx / k.m.irisBPx));
    const yaws = kept.map((k) => k.m.pose.yawDeg);
    const yawMean = yaws.reduce((s, x) => s + x, 0) / Math.max(1, yaws.length);
    const yawSdDeg = Math.sqrt(
      yaws.reduce((s, x) => s + (x - yawMean) ** 2, 0) / Math.max(1, yaws.length - 1),
    );
    const standardErrorMm = (1.2533 * pd.robustSd) / Math.sqrt(Math.max(1, n));
    const failures: string[] = [];
    if (pd.robustSd > st.maxFrameSdMm) failures.push('PER_FRAME_SPREAD');
    if (standardErrorMm > st.maxStandardErrorMm) failures.push('STANDARD_ERROR');
    if (halvesDiffMm > st.maxHalvesDiffMm) failures.push('DRIFT');
    if ((ratio?.robustSd ?? 0) > st.maxIrisRatioSd) failures.push('IRIS_RATIO_UNSTEADY');
    if (yawSdDeg > st.maxYawSdDeg) failures.push('HEAD_MOVING');
    return {
      stable: failures.length === 0,
      perFrameRobustSdMm: pd.robustSd,
      madMm: pd.mad,
      standardErrorMm,
      halvesDiffMm,
      irisRatioRobustSd: ratio?.robustSd ?? 0,
      yawSdDeg,
      failures,
    };
  }

  /** Capture quality only: pixels, sharpness, steadiness. The iris-size assumption is deliberately not part of it. */
  private quality(s: StabilityReport, px: CaptureResult['pixels']): CaptureQuality {
    return this.explain(s, px).grade;
  }

  private explain(s: StabilityReport, px: CaptureResult['pixels']): QualityExplanation {
    return explainQuality({
      perFrameRobustSdMm: s.perFrameRobustSdMm,
      standardErrorMm: s.standardErrorMm,
      minIrisPx: Math.min(px.irisAPx?.median ?? 0, px.irisBPx?.median ?? 0),
      blurSigmaPx: px.blurSigmaPx?.median ?? Infinity,
      processed: this.processed,
      accepted: this.accepted.length,
    });
  }
}
